import NodeSelect from './common/NodeSelect'
import NodeBox from './common/NodeBox'
import NodeCheckBox from './common/NodeCheckBox'
import Message from '@assets/flowchart/Message.svg'
import { Box, Typography } from '@mui/material'
import ChannelCompose from '@/components/notification-send/ChannelCompose'
import MessageTemplateSelect from '@/components/notification-send/MessageTemplateSelect'
import RecipientRow, {
  SendTargets,
} from '@/components/notification-send/RecipientRow'
import { useComposeChecks } from '@/components/notification-send/composeChecks'
import { applyTemplate } from '@/components/notification-send/messageTemplates'
import {
  ChannelDraft,
  ChannelDrafts,
  EMPTY_DRAFT,
} from '@/components/notification-send/sendContent'
import type { SendChannel } from '@/services/notificationService'
import type { ChannelContent } from '@/alerts/treeTypes'
import { useNodeData } from './common/useNodeData'
import { useReference } from '../ReferenceContext'
import { useAppTheme } from '@/theme/ThemeContext'

// The compose part is the send window's (ChannelCompose): one panel per channel
// of the site, ticked panels are sent. The author writes each channel's own
// text under its channel key in `channelContent`; unticked panels keep their
// drafts. The serializer emits `{ node_type:'notification', trigger:{ kind:
// 'on_open' }, notification_channel_keys, channel_content }`.
//
// A channel without a key cannot hold a text of its own, so its panel writes
// the node's `messageBody` (the old common text) instead.
//
// The checkbox emits `run_time_policy` (block Shabbat and festivals). A stored
// policy it cannot express rides in `keptRunTimePolicy` and is saved unchanged.
//
// The frequency and send-time controls are REMOVED from the UI (operator
// decision 2026-07-15), each to return WITH its feature. Their editor-local
// data keys were never serialized, so legacy drafts are unaffected.
const NO_TARGETS: SendTargets = { status: 'pending', phones: [], emails: [] }

type NodeChannel = SendChannel & { key?: string }

const MessageNode = ({ id, data }: { id: string; data: any }) => {
  const patch = useNodeData(id)
  const reference = useReference()
  const { mode } = useAppTheme()
  const labelColor = mode === 'dark' ? '#FFFFFF' : '#003F61'

  const tickedIds: number[] = (data?.channelIds ?? []).map(Number)
  const channels: NodeChannel[] = (
    reference.notificationChannels ?? []
  ).flatMap(c => {
    const channelId = c.notificationChannelId
    const enabled = c.isEnabled !== false
    if (!enabled && !tickedIds.includes(channelId)) return []
    return [
      {
        channelId,
        key: c.notificationChannelKey,
        category: c.category,
        name: enabled ? c.name : `${c.name} (מושבת)`,
      },
    ]
  })
  const content: ChannelContent = data?.channelContent ?? {}
  const draftOf = (c: NodeChannel): ChannelDraft =>
    c.key
      ? { ...EMPTY_DRAFT, ...content[c.key] }
      : { ...EMPTY_DRAFT, body: data?.messageBody ?? '' }
  const drafts: ChannelDrafts = Object.fromEntries(
    channels.map(c => [c.channelId, draftOf(c)])
  )

  const writeDrafts = (next: ChannelDrafts) => {
    const written: ChannelContent = { ...content }
    for (const c of channels) {
      const draft = next[c.channelId] ?? EMPTY_DRAFT
      if (!c.key) {
        if (draft.body !== (data?.messageBody ?? '')) {
          patch({ messageBody: draft.body })
        }
        continue
      }
      const entry = {
        ...(draft.subject !== '' ? { subject: draft.subject } : {}),
        ...(draft.body !== '' ? { body: draft.body } : {}),
      }
      if (Object.keys(entry).length > 0) written[c.key] = entry
      else delete written[c.key]
    }
    patch({ channelContent: written })
  }
  const setTicked = (channelId: number, ticked: boolean) =>
    patch({
      channelIds: (ticked
        ? [...tickedIds.filter(x => x !== channelId), channelId]
        : tickedIds.filter(x => x !== channelId)
      ).map(String),
    })

  // Same guarded convention as the send-dialog entry points (Consumers.tsx):
  // read the raw value first so a missing accountId becomes NaN instead of a
  // silent 0 (Number(null) === 0) that could pass for a real account id.
  const accountIdRaw = localStorage.getItem('accountId')
  const accountId = accountIdRaw ? Number(accountIdRaw) : NaN
  const checks = useComposeChecks(
    accountId,
    !Number.isNaN(accountId),
    channels,
    tickedIds,
    drafts
  )

  const severityOptions = [
    { id: '-10', label: 'נמוכה' },
    { id: '0', label: 'בינונית' },
    { id: '10', label: 'גבוהה' },
  ]

  return (
    <NodeBox
      title="מאפיין שליחה"
      icon={Message}
      nodeId={id}
      endsBranch
      sx={{ width: 960 }}
    >
      <Box sx={{ direction: 'rtl' }}>
        <RecipientRow
          targets={NO_TARGETS}
          templateSelect={
            <MessageTemplateSelect
              onPick={template =>
                writeDrafts(
                  applyTemplate(template, channels, tickedIds, drafts)
                )
              }
            />
          }
        />
        <ChannelCompose
          accountId={accountId}
          channels={channels}
          tickedIds={tickedIds}
          onTick={setTicked}
          drafts={drafts}
          onDraft={(channelId, draft) =>
            writeDrafts({ ...drafts, [channelId]: draft })
          }
          checks={checks}
        />
      </Box>
      <Box
        data-testid="send-node-footer"
        sx={{
          display: 'flex',
          gap: 3,
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          marginTop: '16px',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {data?.keptRunTimePolicy !== undefined && !data?.blockShabbat && (
            <Typography
              sx={{
                color: '#6B7C88',
                fontFamily: 'Rubik, sans-serif',
                fontSize: '12px',
                fontWeight: 300,
              }}
            >
              הגדרה מותאמת נשמרת
            </Typography>
          )}
          <NodeCheckBox
            text="לא לשלוח בשבת ובחגים"
            checked={data?.blockShabbat === true}
            onChange={e =>
              patch({
                blockShabbat: e.target.checked,
                keptRunTimePolicy: undefined,
              })
            }
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <NodeSelect
            placeholder="חומרה"
            options={severityOptions}
            value={data?.severityId ?? '0'}
            onChangeValue={v => patch({ severityId: v })}
          />
          <Typography
            sx={{
              color: labelColor,
              textAlign: 'right',
              fontFamily: 'Rubik, sans-serif',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 300,
              lineHeight: '100%',
            }}
          >
            בחומרה
          </Typography>
        </Box>
      </Box>
    </NodeBox>
  )
}

export default MessageNode
